import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Sparkles, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_CHIP_ACTIVE, CATEGORY_CHIP_INACTIVE } from "@/lib/categoryColors";
import MonthGrid from "@/components/MonthGrid";
import { getNextMonths, groupEventsByDay, toISO } from "@/lib/calendar";

const CATEGORIES = ["Concert", "Festival", "Sport", "Marché", "Repas", "Soirée"];
const MONTHS_WINDOW = 24;

export default function HomePage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commune, setCommune] = useState("");
  const [category, setCategory] = useState("");
  const [communes, setCommunes] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (commune.trim()) params.commune = commune.trim();
      if (category) params.category = category;
      const { data } = await api.get("/events", { params });
      setEvents(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/events/communes").then((r) => setCommunes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commune, category]);

  const months = useMemo(() => getNextMonths(MONTHS_WINDOW), []);
  const eventsByDay = useMemo(() => groupEventsByDay(events), [events]);
  const todayIso = useMemo(() => toISO(new Date()), []);

  return (
    <div className="fade-in">
      {/* Hero */}
      <section
        className="relative overflow-hidden bg-gradient-to-br from-[#FEFCE8] via-[#FEF3C7] to-[#FDE68A] border-b border-[#F0E0CC]"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-[#F0E0CC] text-xs font-semibold text-sage tracking-wider uppercase mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              Annuaire des événements locaux
            </div>
            <h1 className="font-heading font-bold tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Trouvez les événements près de chez vous.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted_text max-w-2xl leading-relaxed">
              Concerts, festivals, marchés, rencontres sportives… découvrez les rendez-vous
              organisés par les mairies et commerçants de votre commune.
            </p>
          </div>

          {/* Search bar */}
          <div className="mt-8 sm:mt-10 bg-white rounded-2xl border border-[#F0E0CC] p-3 sm:p-4 shadow-sm max-w-2xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted_text" />
              <Input
                value={commune}
                onChange={(e) => setCommune(e.target.value)}
                placeholder="Rechercher par commune…"
                list="communes-list"
                className="pl-11 h-12 rounded-xl border-[#F0E0CC] focus-visible:ring-sage/20 focus-visible:border-sage text-base"
                data-testid="search-commune-input"
              />
              <datalist id="communes-list">
                {communes.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          {/* Category chips */}
          <div className="mt-5 -mx-4 sm:mx-0 px-4 sm:px-0">
            <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-2">
              <button
                onClick={() => setCategory("")}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  category === ""
                    ? "bg-[#EAB308] text-white shadow-sm"
                    : "bg-white border border-[#F0E0CC] text-muted_text hover:border-[#EAB308]"
                }`}
                data-testid="filter-all"
              >
                Toutes catégories
              </button>
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c === category ? "" : c)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all border ${
                    category === c
                      ? CATEGORY_CHIP_ACTIVE[c]
                      : CATEGORY_CHIP_INACTIVE[c]
                  }`}
                  data-testid={`filter-${c.toLowerCase()}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Calendar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex items-end justify-between mb-6 sm:mb-8 gap-4">
          <div>
            <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-ink flex items-center gap-2">
              <CalendarDays className="w-7 h-7 text-[#EAB308]" /> Calendrier
            </h2>
            <p className="text-sm text-muted_text mt-1">
              {loading
                ? "Chargement…"
                : `${events.length} événement${events.length > 1 ? "s" : ""} sur les ${MONTHS_WINDOW} prochains mois. Cliquez sur un jour pour voir les détails.`}
            </p>
          </div>
        </div>

        {/* Legend */}
        <div className="mb-6 flex flex-wrap gap-3 text-xs">
          {CATEGORIES.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 text-muted_text">
              <span
                className={`w-2.5 h-2.5 rounded-full ${
                  {
                    Concert: "bg-[#E55934]",
                    Festival: "bg-[#DC2626]",
                    Sport: "bg-[#2563EB]",
                    Marché: "bg-[#16A34A]",
                    Repas: "bg-[#7C3AED]",
                    Soirée: "bg-neutral-400",
                  }[c]
                }`}
              />
              {c}
            </span>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-96 bg-white rounded-2xl border border-[#FDE68A] animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" data-testid="calendar-months">
            {months.map(({ year, month }) => (
              <MonthGrid
                key={`${year}-${month}`}
                year={year}
                month={month}
                eventsByDay={eventsByDay}
                todayIso={todayIso}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
